import {
  useCallback, useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, ArrowUp, Camera, Film, Loader2, Paperclip, Square, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useUploadFile } from '@/hooks/use-upload-file';
import { cn } from '@/lib/utils';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  acceptAttachmentFiles,
  type AttachmentKind,
  type AttachmentRejection,
} from '@/lib/ai-assistant/attachmentRules';

const MAX_LENGTH = 4000;

export interface SentAttachment {
  kind: AttachmentKind;
  url: string;
  name: string;
  mime: string;
  size: number;
}

interface DraftAttachment {
  id: string;
  kind: AttachmentKind;
  name: string;
  mime: string;
  size: number;
  previewUrl: string;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  url: string | null;
}

interface Props {
  // True while a message is being sent or the assistant is answering.
  disabled: boolean;
  onSend: (text: string, attachments: SentAttachment[]) => Promise<boolean>;
  // While `disabled`, Send becomes a Stop button calling this.
  onStop?: () => void;
  stopping?: boolean;
  /** Present while an editor is open: attaches the frame it shows. */
  onCaptureFrame?: () => Promise<{ url: string; name: string } | null>;
}

// Previews of picked files are blob URLs; a captured frame previews by its URL.
const releasePreview = (url: string) => {
  if (url.startsWith('blob:')) URL.revokeObjectURL(url);
};

// Enter sends, Shift+Enter adds a line. Images and videos can be attached with
// the clip, pasted or dropped; they upload right away and the message waits
// for them. The box empties the moment a message is sent; if sending fails,
// the text and attachments come back unless the user already started anew.
export function AssistantComposer({
  disabled, onSend, onStop, stopping = false, onCaptureFrame,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { getUploadUrl } = useUploadFile();
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<DraftAttachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Uploads run one at a time (the upload hook tracks a single request).
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  // Ids still attached, updated immediately (state lags until the next render):
  // a queued upload skips a file removed before its turn.
  const liveIdsRef = useRef(new Set<string>());

  useEffect(() => () => {
    attachmentsRef.current.forEach((entry) => releasePreview(entry.previewUrl));
  }, []);

  const text = draft.trim();
  const uploading = attachments.some((entry) => entry.status === 'uploading');
  const ready = attachments.filter((entry) => entry.status === 'ready' && entry.url);
  const canSend = !disabled && !uploading && text.length <= MAX_LENGTH
    && (text.length > 0 || ready.length > 0);

  const update = (id: string, patch: Partial<DraftAttachment>) => {
    setAttachments((current) => current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  };

  const addFiles = useCallback((files: File[]) => {
    if (!files.length) return;
    const { accepted, rejected } = acceptAttachmentFiles(files, attachmentsRef.current);
    const reasons = Array.from(new Set(rejected));
    reasons.forEach((reason: AttachmentRejection) => {
      toast({ title: t(`aiAssistant.attach.errors.${reason}`), variant: 'destructive' });
    });
    if (!accepted.length) return;
    const added = accepted.map(({ file, kind }) => ({
      file,
      entry: {
        id: crypto.randomUUID(),
        kind,
        name: file.name || (kind === 'image' ? 'imagem' : 'vídeo'),
        mime: file.type,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
        status: 'uploading' as const,
        progress: 0,
        url: null,
      },
    }));
    added.forEach(({ entry }) => liveIdsRef.current.add(entry.id));
    setAttachments((current) => [...current, ...added.map(({ entry }) => entry)]);
    added.forEach(({ file, entry }) => {
      queueRef.current = queueRef.current.then(async () => {
        if (!liveIdsRef.current.has(entry.id)) return;
        try {
          const url = await getUploadUrl(file, (progress) => update(entry.id, { progress: progress.percentage }));
          update(entry.id, url ? { status: 'ready', url, progress: 100 } : { status: 'error' });
        } catch {
          update(entry.id, { status: 'error' });
        }
      });
    });
  }, [getUploadUrl, t, toast]);

  const captureFrame = async () => {
    if (!onCaptureFrame || capturing) return;
    setCapturing(true);
    try {
      const frame = await onCaptureFrame();
      if (!frame) {
        toast({ title: t('aiAssistant.attach.errors.capture_failed'), variant: 'destructive' });
        return;
      }
      const entry: DraftAttachment = {
        id: crypto.randomUUID(),
        kind: 'image',
        name: frame.name,
        mime: 'image/jpeg',
        size: 0,
        previewUrl: frame.url,
        status: 'ready',
        progress: 100,
        url: frame.url,
      };
      liveIdsRef.current.add(entry.id);
      setAttachments((current) => [...current, entry]);
    } finally {
      setCapturing(false);
    }
  };

  const remove = (id: string) => {
    liveIdsRef.current.delete(id);
    setAttachments((current) => {
      const gone = current.find((entry) => entry.id === id);
      if (gone) releasePreview(gone.previewUrl);
      return current.filter((entry) => entry.id !== id);
    });
  };

  const submit = async () => {
    if (!canSend) return;
    const sentText = text;
    const sentDrafts = ready;
    setDraft('');
    sentDrafts.forEach((entry) => liveIdsRef.current.delete(entry.id));
    setAttachments((current) => current.filter((entry) => !sentDrafts.includes(entry)));
    const sent = await onSend(sentText, sentDrafts.map((entry) => ({
      kind: entry.kind, url: entry.url as string, name: entry.name, mime: entry.mime, size: entry.size,
    })));
    if (sent) {
      sentDrafts.forEach((entry) => releasePreview(entry.previewUrl));
      return;
    }
    setDraft((current) => (current.trim() ? current : sentText));
    setAttachments((current) => {
      if (current.length) return current;
      sentDrafts.forEach((entry) => liveIdsRef.current.add(entry.id));
      return sentDrafts;
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submit();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData?.files ?? []);
    if (!files.length) return;
    event.preventDefault();
    addFiles(files);
  };

  const onDrop = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDragging(false);
    addFiles(Array.from(event.dataTransfer?.files ?? []));
  };

  return (
    <form
      className={cn('relative rounded-md', dragging && 'ring-2 ring-primary')}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      onDragOver={(event) => {
        if (!event.dataTransfer?.types?.includes('Files')) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2" data-testid="assistant-attachments">
          {attachments.map((entry) => (
            <div
              key={entry.id}
              className="relative h-14 w-14 overflow-hidden rounded-md border border-border bg-muted"
              title={entry.name}
              data-testid="assistant-attachment"
            >
              {entry.kind === 'image' ? (
                <img src={entry.previewUrl} alt={entry.name} className="h-full w-full object-cover" />
              ) : (
                <>
                  <video src={entry.previewUrl} muted preload="metadata" className="h-full w-full object-cover" />
                  <Film className="absolute bottom-1 left-1 h-3.5 w-3.5 text-white drop-shadow" />
                </>
              )}
              {entry.status === 'uploading' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-[10px] text-white">
                  <Loader2 className="mr-0.5 h-3 w-3 animate-spin" />
                  {Math.round(entry.progress)}%
                </div>
              )}
              {entry.status === 'error' && (
                <div className="absolute inset-0 flex items-center justify-center bg-destructive/60" title={t('aiAssistant.attach.errors.upload_failed')}>
                  <AlertCircle className="h-4 w-4 text-white" />
                </div>
              )}
              <button
                type="button"
                onClick={() => remove(entry.id)}
                className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                aria-label={t('aiAssistant.attach.remove')}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <Textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value.slice(0, MAX_LENGTH))}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        placeholder={t('aiAssistant.placeholder')}
        aria-label={t('aiAssistant.placeholder')}
        rows={2}
        className="max-h-40 min-h-[64px] resize-none pb-9 pr-12 text-sm"
        data-testid="assistant-input"
      />
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPTED_ATTACHMENT_TYPES.join(',')}
        className="hidden"
        data-testid="assistant-file-input"
        onChange={(event) => {
          addFiles(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      {onCaptureFrame && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="absolute bottom-2 left-9 h-7 w-7 text-muted-foreground"
          onClick={() => { void captureFrame(); }}
          disabled={capturing}
          aria-label={t('aiAssistant.attach.captureFrame')}
          title={t('aiAssistant.attach.captureFrame')}
          data-testid="assistant-capture-frame"
        >
          {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
        </Button>
      )}
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="absolute bottom-2 left-2 h-7 w-7 text-muted-foreground"
        onClick={() => inputRef.current?.click()}
        aria-label={t('aiAssistant.attach.button')}
        title={t('aiAssistant.attach.button')}
        data-testid="assistant-attach"
      >
        <Paperclip className="h-4 w-4" />
      </Button>
      {disabled && onStop ? (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          disabled={stopping}
          onClick={onStop}
          className="absolute bottom-2 right-2 h-8 w-8 rounded-full"
          aria-label={t('aiAssistant.stop')}
          title={t('aiAssistant.stop')}
          data-testid="assistant-stop"
        >
          {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-3.5 w-3.5 fill-current" />}
        </Button>
      ) : (
        <Button
          type="submit"
          size="icon"
          disabled={!canSend}
          className="absolute bottom-2 right-2 h-8 w-8 rounded-full"
          aria-label={t('aiAssistant.send')}
          data-testid="assistant-send"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      )}
    </form>
  );
}
